import React from 'react';
import { Box, Typography, List, ListItem, ListItemText, ListItemAvatar, Avatar, Divider, Pagination, Button } from '@mui/material';
import { api } from '../../configuration/api/apiHelper';
import { MessageViewModel } from '../../configuration/api/apiSchema';
import { formatDistanceToNow } from 'date-fns';
import { GlassContainer, GlassCard, SectionHeader } from '../../components/glassmorphism/GlassmorphismStyles';

const NotificationsPage: React.FC = () => {
  const [items, setItems] = React.useState<MessageViewModel[]>([]);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [totalPages, setTotalPages] = React.useState(1);
  const pageSize = 10;

  const fetchPage = React.useCallback(async (page: number) => {
    const res = await api.notification.postNotification({ currentPage: page, pageSize });
    setItems(res.data?.items ?? []);
    setTotalPages(res.data?.totalPages ?? 1);
    setCurrentPage(res.data?.currentPage ?? page);
  }, []);

  React.useEffect(() => {
    fetchPage(1);
  }, [fetchPage]);

  const markAllAsRead = async () => {
    await api.notification.readAllMessagesUpdate();
    fetchPage(currentPage);
  };

  const markOneAsViewed = async (id?: string) => {
    if (!id) return;
    await api.notification.viewMessageUpdate({ id });
    setItems(prev => prev.map(i => (i.id === id ? { ...i, isViewed: true } : i)));
  };

  const deleteOne = async (id?: string) => {
    if (!id) return;
    await api.notification.notificationDelete(id);
    fetchPage(currentPage);
  };

  return (
    <GlassContainer>
      <Box width={{ xs: '90%', sm: '80%', md: '75%' }} mx="auto" mt={12} pb={4}>
        <GlassCard sx={{ p: { xs: 2, sm: 4 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <SectionHeader>Notifications</SectionHeader>
            <Button variant="outlined" size="small" onClick={markAllAsRead} disabled={!items.some(i => !i.isViewed)}>Mark all as read</Button>
          </Box>

          <List>
            {items.map((n) => (
              <React.Fragment key={n.id}>
                <ListItem
                  alignItems="flex-start"
                  sx={{ bgcolor: n.isViewed ? 'transparent' : 'rgba(25, 118, 210, 0.04)' }}
                  secondaryAction={
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {!n.isViewed && (
                        <Button size="small" onClick={() => markOneAsViewed(n.id)}>Mark read</Button>
                      )}
                      <Button size="small" color="error" onClick={() => deleteOne(n.id)}>Delete</Button>
                    </Box>
                  }
                >
                  <ListItemAvatar>
                    <Avatar>🔔</Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={n.processType || 'Notification'}
                    secondary={
                      <>
                        <Typography variant="body2" color="text.primary">{n.message}</Typography>
                        <Typography variant="caption" color="text.secondary">{n.createdAt ? formatDistanceToNow(new Date(n.createdAt), { addSuffix: true }) : ''}</Typography>
                      </>
                    }
                  />
                </ListItem>
                <Divider component="li" />
              </React.Fragment>
            ))}
          </List>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination count={totalPages} page={currentPage} onChange={(_, p) => fetchPage(p)} />
          </Box>
        </GlassCard>
      </Box>
    </GlassContainer>
  );
};

export default NotificationsPage;

